import type { ReactNode } from 'react';
import { useAuthStore } from '@/lib/auth/auth-store';

/**
 * True if the user holds ANY of the given permissions (no args = always true).
 * Any-of is the useful default for UI gating ("show if they can do X or Y"). This
 * is UX only — the backend `@RequirePermission` guards are the real gate.
 */
export function useCan(...permissions: string[]): boolean {
  const user = useAuthStore((s) => s.user);
  if (permissions.length === 0) return true;
  const held = new Set(user?.permissions ?? []);
  return permissions.some((p) => held.has(p));
}

interface CanProps {
  /** A single permission or a list (any-of). */
  permission: string | string[];
  children: ReactNode;
  /** Rendered when the permission is absent (default: nothing). */
  fallback?: ReactNode;
}

/** Render `children` only if the user holds the permission(s); else `fallback`. */
export function Can({ permission, children, fallback = null }: CanProps): ReactNode {
  const perms = Array.isArray(permission) ? permission : [permission];
  return <>{useCan(...perms) ? children : fallback}</>;
}
